// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import GlassToasterLayer from '@components/design-system/GlassToasterLayer';
import { QuotaExceededToastBridge } from '@components/QuotaExceededToastBridge';
import { ThemeProvider } from '@components/ThemeProvider';
import { I18nProvider } from '@components/I18nProvider';
import { AppSurfaceProvider } from './rendering/AppSurfaceProvider';
import { registerChunkLoadRecovery } from '@utils/chunkLoadRecovery';
import { initWebVitals } from '@utils/webVitals';
import './index.css';
import './styles/v4-subpages.css';
import './styles/homepage.css';
import './styles/homepage-blocks.css';
import './styles/agent-workspaces.css';

const root = ReactDOM.createRoot(document.getElementById('root')!);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

registerChunkLoadRecovery();
initWebVitals();

function bootstrap() {
  root.render(
    <React.StrictMode>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <I18nProvider>
            <ThemeProvider>
              {/* 页面渲染策略（只读派生）：必须同时包住 App 与 Toaster——Toaster 是 App 的
                  兄弟节点，不在 App 子树内，否则读不到工作区策略。
                  见 rendering/AppSurfaceProvider.tsx。 */}
              <AppSurfaceProvider>
                <QuotaExceededToastBridge />
                <App />
                {/* 批次 8 收口：Toaster 挂进已有页面舞台（fixed 定位、视觉位置不变、零新建上下文），
                    由材质桥升级为真玻璃；页面顶部没有可用舞台时（营销首屏/登录页）就地回落 CSS 毛玻璃。
                    详见 design-system/GlassToasterLayer.tsx。 */}
                <GlassToasterLayer>
                  <Toaster position="top-center" richColors closeButton />
                </GlassToasterLayer>
              </AppSurfaceProvider>
            </ThemeProvider>
          </I18nProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

void bootstrap();
